import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import {
  connectIntegration,
  disconnectIntegration,
  getAvailableIntegrations,
  getIntegrations as getConnectedIntegrations,
  getIntegrationByKey,
  getOAuthUrl,
  updateIntegrationApiKey,
  updateIntegrationConfig,
  triggerIntegrationSyncRun,
  getIntegrationTasks,
  fetchIntegrationObjectTasks,
  updateIntegrationObjectMeta,
  getUnprocessedItemCount,
  processUnprocessedItems,
  getMissingOrganizationsCount,
  fillMissingOrganizations,
  getIntegrationExploreFields,
  prepareIntegrationMeta,
  testIntegrationCredentials,
  getMLProcessedCount,
  sendMLItemUpdate,
  updateExternalObject,
  IntegrationTask,
  TasksResponse,
  ExploreFieldsResponse,
  PrepareMetaResponse,
  TestCredentialsResponse,
  MLProcessedCountResponse,
  MLItemUpdateRequest,
  MLItemUpdateResponse,
  UpdateExternalObjectRequest,
  UpdateExternalObjectResponse
} from "../api/integrations2.0";
import { BagelObjectConf, ExternalObjectConf, Integration } from "@bageltechnologies/bagel-common";

// Query keys
export const integrationKeys = {
  all: ["integrations"] as const,
  available: () => [...integrationKeys.all, "available"] as const,
  connected: () => [...integrationKeys.all, "connected"] as const,
  detail: (key: string) => [...integrationKeys.all, "detail", key] as const,
};

// Standardized OAuth completion
export const completeOAuth = async (
  authToken: string,
  key: string,
  code: string,
  subdomain?: string
): Promise<any> => {
  const config = {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  };

  try {
    // First, get the integration details to check if subdomain is required
    const integrationResponse = await axios.get(
      `${window.__RUNTIME_CONFIG__.REACT_APP_API_DOMAIN}/integrations/v2/${key}`,
      // `${window.__RUNTIME_CONFIG__.REACT_APP_API_DOMAIN}/authorize/oauth2`,
      config
    );
    
    const integration = integrationResponse.data;
    
    // Check if subdomain is required based on integration configuration
    if (integration?.available?.oauth?.requiresSubdomain && !subdomain) {
      throw new Error(`Subdomain is required for ${key} integration`);
    }

    const response = await axios.post(
      `${window.__RUNTIME_CONFIG__.REACT_APP_API_DOMAIN}/authorize/oauth2`,
      { key: key, code, subdomain },
      config
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Hooks
export const useAvailableIntegrations = () => {
  const auth0 = useAuth0();
  
  return useQuery({
    queryKey: integrationKeys.available(),
    queryFn: () => getAvailableIntegrations(auth0),
    enabled: !!auth0,
    refetchInterval: 15000, // Refetch every 15 seconds
    refetchIntervalInBackground: false, // Don't refetch when tab is not active
    staleTime: 10000, // Consider data fresh for 10 seconds
    refetchOnWindowFocus: true, // Refetch when user returns to tab
  });
};

export const useConnectedIntegrations = () => {
  const auth0 = useAuth0();
  
  return useQuery({
    queryKey: integrationKeys.connected(),
    queryFn: () => getConnectedIntegrations(auth0),
    enabled: !!auth0,
    refetchInterval: 15000, // Refetch every 15 seconds
    refetchIntervalInBackground: false, // Don't refetch when tab is not active
    staleTime: 10000, // Consider data fresh for 10 seconds
    refetchOnWindowFocus: true, // Refetch when user returns to tab
  });
};

export const useIntegration = (key: string) => {
  const auth0 = useAuth0();
  
  return useQuery({
    queryKey: integrationKeys.detail(key),
    queryFn: () => getIntegrationByKey(key, auth0),
    enabled: !!auth0 && !!key,
    refetchInterval: 15000, // Refetch every 15 seconds
    refetchIntervalInBackground: false, // Don't refetch when tab is not active
    staleTime: 10000, // Consider data fresh for 10 seconds
    refetchOnWindowFocus: true, // Refetch when user returns to tab
  });
};

export const useIntegrationConfig = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      key,
      config
    }: {
      key: string;
      config: Partial<Omit<Integration, 'accountId'>> & { accountId?: string };
    }) => updateIntegrationConfig(key, config as Integration, auth0),
    onSuccess: (_, { key }) => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.detail(key) });
      queryClient.invalidateQueries({ queryKey: integrationKeys.connected() });
    },
  });
};

export const useIntegrationConnection = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  const connectMutation = useMutation({
    mutationFn: ({
      key,
      code,
    }: {
      key: string;
      code: string;
    }) => connectIntegration(key, code, auth0),
    onSuccess: (data, { key }) => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.detail(key) });
      queryClient.invalidateQueries({ queryKey: integrationKeys.connected() });
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: (key: string) => disconnectIntegration(key, auth0),
    onSuccess: (_, key) => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.detail(key) });
      queryClient.invalidateQueries({ queryKey: integrationKeys.connected() });
    },
  });

  const getOAuthUrlMutation = useMutation({
    mutationFn: (key: string) => getOAuthUrl(key, auth0),
  });

  return {
    connect: connectMutation.mutateAsync,
    disconnect: disconnectMutation.mutateAsync,
    getOAuthUrl: getOAuthUrlMutation.mutateAsync,
    isConnecting: connectMutation.isPending,
    isDisconnecting: disconnectMutation.isPending,
    isGettingOAuthUrl: getOAuthUrlMutation.isPending,
  };
};

export const useExternalObjects = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      key,
      externalObjects,
    }: {
      key: string;
      externalObjects: ExternalObjectConf[];
    }) => updateIntegrationConfig(key, { externalObjects } as unknown as Integration, auth0),
    onSuccess: (_, { key }) => {
      // Invalidate relevant queries to refresh the data
      queryClient.invalidateQueries({ queryKey: integrationKeys.detail(key) });
      queryClient.invalidateQueries({ queryKey: integrationKeys.connected() });
    },
  });
};

export const useIntegrationApiKey = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      key,
      tokenObject,
    }: {
      key: string;
      tokenObject: any;
    }) => updateIntegrationApiKey(key, auth0, tokenObject),
    onSuccess: (_, { key }) => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.detail(key) });
      queryClient.invalidateQueries({ queryKey: integrationKeys.connected() });
    },
  });
};

/**
 * Hook to update integration object metadata by triggering a metadata sync.
 * @returns {object} { mutateAsync, isLoading, isError, error, data }
 */
export const useUpdateIntegrationObjectMeta = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ key, object }: { key: string; object: string }) =>
      updateIntegrationObjectMeta(key, object, auth0),
    onSuccess: (_, { key }) => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.detail(key) });
      queryClient.invalidateQueries({ queryKey: integrationKeys.connected() });
    },
  });
};

/**
 * Hook to trigger a sync run for one or more objects of an integration.
 * @returns {object} { triggerSyncRun, isLoading, isError, error, data }
 */
export const useTriggerIntegrationSyncRun = () => {
  const auth0 = useAuth0();
  return useMutation({
    mutationFn: ({ 
      key, 
      objects, 
      dateRange 
    }: { 
      key: string; 
      objects: string[];
      dateRange?: {
        start: string;
        end: string;
        dateField?: string;
      };
    }) => triggerIntegrationSyncRun(auth0, key, objects, dateRange),
  });
};

/**
 * Hook to fetch a paginated list of tasks for an integration.
 * @param key Integration key (e.g., 'salesforce', 'zendesk')
 * @param skip How many tasks to skip (for pagination, default 0)
 * @param limit How many tasks to return (default 20)
 * @param status Optional status parameter for filtering tasks (can be comma-separated string)
 * @returns {object} { data: TasksResponse, isLoading, isError, error, refetch }
 */
export const useIntegrationTasks = (
  key: string,
  skip = 0,
  limit = 20,
  status?: string
) => {
  const auth0 = useAuth0();
  return useQuery({
    queryKey: ["integrationTasks", key, skip, limit, status],
    queryFn: () => getIntegrationTasks(auth0, key, skip, limit, status),
    enabled: !!auth0 && !!key,
    refetchInterval: 15000, // Refetch every 15 seconds
    refetchIntervalInBackground: false, // Don't refetch when tab is not active
    staleTime: 10000, // Consider data fresh for 10 seconds
    refetchOnWindowFocus: true, // Refetch when user returns to tab
  });
};

/**
 * Hook to fetch paginated tasks for a specific integration object.
 * @param key Integration key (e.g., 'salesforce')
 * @param object Object name (e.g., 'accounts')
 * @param skip How many tasks to skip (for pagination, default 0)
 * @param limit How many tasks to return (default 20)
 * @param status Optional status parameter for filtering tasks (can be comma-separated string)
 * @returns {object} { data: TasksResponse, isLoading, isError, error, refetch }
 */
export const useIntegrationObjectTasks = (
  key: string,
  object: string,
  skip = 0,
  limit = 20,
  status?: string
) => {
  const auth0 = useAuth0();
  return useQuery({
    queryKey: ["integrationObjectTasks", key, object, skip, limit, status],
    queryFn: () => fetchIntegrationObjectTasks(auth0, key, object, skip, limit, status),
    enabled: !!auth0 && !!key && !!object,
    refetchInterval: 15000, // Refetch every 15 seconds
    refetchIntervalInBackground: false, // Don't refetch when tab is not active
    staleTime: 10000, // Consider data fresh for 10 seconds
    refetchOnWindowFocus: true, // Refetch when user returns to tab
  });
};

/**
 * Hook to get the count of processed and unprocessed items for a given integration and object.
 * @param key Integration key (e.g., 'airtable')
 * @param originObjectKey Object name (e.g., 'survey')
 * @returns {object} { data, isLoading, isError, error, refetch } where data = { processed, unprocessed }
 */
export const useUnprocessedItemCount = (
  key: string,
  originObjectKey: string
) => {
  const auth0 = useAuth0();
  return useQuery({
    queryKey: ["unprocessedItemCount", key, originObjectKey],
    queryFn: () => getUnprocessedItemCount(auth0, key, originObjectKey),
    enabled: !!auth0 && !!key && !!originObjectKey,
    refetchInterval: 15000, // Refetch every 15 seconds
    refetchIntervalInBackground: false, // Don't refetch when tab is not active
    staleTime: 10000, // Consider data fresh for 10 seconds
    refetchOnWindowFocus: true, // Refetch when user returns to tab
  });
};

/**
 * Hook to process all unprocessed items for a given integration and object.
 * @returns {object} { mutateAsync, isLoading, isError, error, data }
 */
export const useProcessUnprocessedItems = () => {
  const auth0 = useAuth0();
  return useMutation({
    mutationFn: ({ key, originObjectKey }: { key: string; originObjectKey: string }) =>
      processUnprocessedItems(auth0, key, originObjectKey),
  });
};

/**
 * Hook to get the count of missing organizations for a given integration and object.
 * @param key Integration key (e.g., 'salesforce')
 * @param object Object2.0 name (e.g., 'opportunity')
 * @param originObjectKey Origin object key from external system (e.g., 'Opportunity')
 * @returns {object} { data, isLoading, isError, error, refetch } where data contains missing orgs count info
 */
export const useMissingOrganizationsCount = (
  key: string,
  object: string,
  originObjectKey: string
) => {
  const auth0 = useAuth0();
  return useQuery({
    queryKey: ["missingOrganizationsCount", key, object, originObjectKey],
    queryFn: () => getMissingOrganizationsCount(auth0, key, object, originObjectKey),
    enabled: !!auth0 && !!key && !!object && !!originObjectKey && object !== 'account',
    refetchInterval: 15000, // Refetch every 15 seconds
    refetchIntervalInBackground: false, // Don't refetch when tab is not active
    staleTime: 10000, // Consider data fresh for 10 seconds
    refetchOnWindowFocus: true, // Refetch when user returns to tab
  });
};

/**
 * Hook to fill missing organizations for a given integration and object.
 * @returns {object} { mutateAsync, isLoading, isError, error, data }
 */
export const useFillMissingOrganizations = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      key, 
      object, 
      originObjectKey 
    }: { 
      key: string; 
      object: string; 
      originObjectKey: string;
    }) => fillMissingOrganizations(auth0, key, object, originObjectKey),
    onSuccess: (_, { key, object, originObjectKey }) => {
      // Invalidate the missing organizations count query to refresh the data
      queryClient.invalidateQueries({ 
        queryKey: ["missingOrganizationsCount", key, object, originObjectKey] 
      });
      // Also invalidate related integration queries
      queryClient.invalidateQueries({ queryKey: integrationKeys.detail(key) });
    },
  });
};

/**
 * Hook to get complete field metadata with statistics and values for a specific integration object.
 * Only fetches data when enabled (typically when modal opens).
 * @param key Integration key (e.g., 'salesforce', 'jira')
 * @param object Object name (e.g., 'account', 'opportunity')
 * @param originObjectKey Origin object key from external system (e.g., 'Account', 'Opportunity')
 * @param enabled Whether to fetch the data (should be false until modal opens)
 * @returns {object} { data: ExploreFieldsResponse, isLoading, isError, error, refetch }
 */
export const useIntegrationExploreFields = (
  key: string,
  object: string,
  originObjectKey: string,
  enabled = false
) => {
  const auth0 = useAuth0();
  return useQuery({
    queryKey: ["integrationExploreFields", key, object, originObjectKey],
    queryFn: () => getIntegrationExploreFields(key, object, originObjectKey, auth0),
    enabled: !!auth0 && !!key && !!object && !!originObjectKey && enabled,
    refetchInterval: 15000, // Refetch every 15 seconds
    refetchIntervalInBackground: false, // Don't refetch when tab is not active
    staleTime: 10000, // Consider data fresh for 10 seconds
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });
};

/**
 * Hook to prepare integration metadata by syncing and fetching available objects.
 * @returns {object} { mutateAsync, isLoading, isError, error, data }
 */
export const usePrepareIntegrationMeta = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (key: string) => prepareIntegrationMeta(key, auth0),
    onSuccess: (_, key) => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.detail(key) });
      queryClient.invalidateQueries({ queryKey: integrationKeys.connected() });
    },
  });
};

/**
 * Hook to test integration credentials and save the test result.
 * @returns {object} { mutateAsync, isLoading, isError, error, data }
 */
export const useTestIntegrationCredentials = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (key: string) => testIntegrationCredentials(key, auth0),
    onSuccess: (_, key) => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.detail(key) });
      queryClient.invalidateQueries({ queryKey: integrationKeys.connected() });
    },
  });
};

/**
 * Hook to get ML processed count for a specific integration object.
 * @param key Integration key (e.g., 'salesforce', 'zendesk')
 * @param originObjectKey Object name (e.g., 'Case', 'Ticket')
 * @param enabled Whether to fetch the data (default true)
 * @returns {object} { data: MLProcessedCountResponse, isLoading, isError, error, refetch }
 */
export const useMLProcessedCount = (
  key: string,
  originObjectKey: string,
  enabled = true
) => {
  const auth0 = useAuth0();
  return useQuery({
    queryKey: ["mlProcessedCount", key, originObjectKey],
    queryFn: () => getMLProcessedCount(key, originObjectKey, auth0),
    enabled: !!auth0 && !!key && !!originObjectKey && enabled,
    refetchInterval: 15000, // Refetch every 15 seconds
    refetchIntervalInBackground: false, // Don't refetch when tab is not active
    staleTime: 10000, // Consider data fresh for 10 seconds
    refetchOnWindowFocus: true, // Refetch when user returns to tab
  });
};

/**
 * Hook to send ML item update for a specific integration object.
 * @returns {object} { mutateAsync, isLoading, isError, error, data }
 */
export const useSendMLItemUpdate = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      key, 
      originObjectKey, 
      updateData 
    }: { 
      key: string; 
      originObjectKey: string; 
      updateData: MLItemUpdateRequest;
    }) => sendMLItemUpdate(key, originObjectKey, updateData, auth0),
    onSuccess: (_, { key, originObjectKey }) => {
      // Invalidate the ML processed count query to refresh the data
      queryClient.invalidateQueries({ 
        queryKey: ["mlProcessedCount", key, originObjectKey] 
      });
      // Also invalidate related integration queries
      queryClient.invalidateQueries({ queryKey: integrationKeys.detail(key) });
    },
  });
};

/**
 * Hook to update an external object configuration for a specific integration.
 * @returns {object} { mutateAsync, isLoading, isError, error, data }
 */
export const useUpdateExternalObject = () => {
  const auth0 = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      key, 
      originObjectKey, 
      updates 
    }: { 
      key: string; 
      originObjectKey: string; 
      updates: UpdateExternalObjectRequest;
    }) => updateExternalObject(key, originObjectKey, updates, auth0),
    onSuccess: (_, { key }) => {
      // Invalidate integration queries to refresh the data
      queryClient.invalidateQueries({ queryKey: integrationKeys.detail(key) });
      queryClient.invalidateQueries({ queryKey: integrationKeys.connected() });
    },
  });
};